import { t } from 'i18next';
import cn from 'classnames';

import TipSelection from '../TipSelection';

import { getShoppingCartTotal } from '~models/shoppingCart';
import { formatPrice, deliveryPrice } from '~utils/number';
import { isEmptyObject } from '~utils/object';
import { getCouponsTotal } from '~utils/papaPoints';
import Snackbar from '~components/Snackbar';

import ListItem from './components/ListItem';
import { ORDER_DETAIL_ITEMS_ID } from './constants';
import styles from './styles.module.scss';

const todayIsDeliveryDay = (daysOfFreeDelivery, today) => daysOfFreeDelivery?.some(day => day === today);

export const getRemainingToGetDelivery = (minValueForDelivery, shoppingCart) =>
  minValueForDelivery -
  ((shoppingCart?.subtotal || 0) - (shoppingCart?.discount || 0) + (shoppingCart?.taxCharge || 0));

export const levelHasFreeDelivery = actualLevel =>
  actualLevel?.loyaltyBenefits?.some(
    benefit =>
      !isEmptyObject(benefit?.freeDeliveryDays) &&
      todayIsDeliveryDay(
        benefit?.freeDeliveryDays,
        new Date().toLocaleString('en-us', { weekday: 'long' }).toLowerCase()
      )
  );

export const getDiscounts = (shoppingCart, isGiftCard, amount) => {
  const couponsDiscount = getCouponsTotal(shoppingCart);
  const giftCardsDiscount = isGiftCard ? Math.min(amount, getShoppingCartTotal(shoppingCart)) : 0;
  return {
    couponsDiscount,
    giftCardsDiscount,
    loyaltyDiscount: shoppingCart?.discount - couponsDiscount
  };
};

export const getTaxesInformation = shoppingCart =>
  shoppingCart?.appliedTaxes?.map(tax => ({
    name: tax?.description,
    charge: tax?.charge
  }));

export const getOrderDetailItems = ({
  subtotal,
  loyaltyDiscount,
  showLoyaltyDiscount,
  taxes = [],
  delivery,
  showDeliveryInformation,
  showMinDeliveryFreeText,
  minimumValueForFreeDelivery,
  discountForRedeemedCode,
  showDiscountForRedeemedCode,
  showDiscountAnimation,
  tip,
  showTip,
  textMinimumAmount,
  animateDisclaimer,
  disclaimerMinDeliveryActive,
  isMobile,
  selectedPaymentMethod,
  isPapaCar,
  pointsSpent,
  showPointsSpent
}) => [
  {
    id: ORDER_DETAIL_ITEMS_ID.MINIMUM_DELIVERY,
    Component: Snackbar,
    componentProps: {
      classNames: cn('m-bottom-4', styles.textMinAmountContainer, {
        [styles.animateDisclaimer]: animateDisclaimer,
        center: !isMobile
      }),
      textComponent: textMinimumAmount,
      hide: !disclaimerMinDeliveryActive,
      borderAdornementPosition: 'top'
    }
  },
  {
    id: ORDER_DETAIL_ITEMS_ID.SUBTOTAL,
    Component: ListItem,
    componentProps: {
      description: t(`CheckoutOrderDetail:${ORDER_DETAIL_ITEMS_ID.SUBTOTAL}`),
      amount: formatPrice(subtotal)
    }
  },
  {
    id: ORDER_DETAIL_ITEMS_ID.PAPA_POINTS_BENEFITS,
    Component: ListItem,
    componentProps: {
      description: t(`CheckoutOrderDetail:${ORDER_DETAIL_ITEMS_ID.PAPA_POINTS_BENEFITS}`),
      amount: `- ${formatPrice(loyaltyDiscount)}`,
      amountClassName: styles.discount,
      hide: !showLoyaltyDiscount
    }
  },
  {
    id: ORDER_DETAIL_ITEMS_ID.TOTAL_SAVED,
    Component: ListItem,
    componentProps: {
      className: showDiscountAnimation && styles.appearDiscount,
      description: t(`CheckoutOrderDetail:${ORDER_DETAIL_ITEMS_ID.TOTAL_SAVED}`),
      amount: `- ${formatPrice(discountForRedeemedCode)}`,
      amountClassName: styles.discount,
      hide: !showDiscountForRedeemedCode
    }
  },
  {
    id: ORDER_DETAIL_ITEMS_ID.DELIVERY,
    Component: ListItem,
    componentProps: {
      description: t(`CheckoutOrderDetail:${ORDER_DETAIL_ITEMS_ID.DELIVERY}`),
      amount: deliveryPrice(delivery),
      hide: !showDeliveryInformation
    }
  },
  ...taxes?.map(({ name, charge }) => ({
    id: `${ORDER_DETAIL_ITEMS_ID.TAX}-${name}`,
    Component: ListItem,
    componentProps: {
      description: `${name}:`,
      amount: formatPrice(charge),
      hide: !charge
    }
  })),
  {
    id: ORDER_DETAIL_ITEMS_ID.MIN_DELIVERY_FEE_TEXT,
    Component: ListItem,
    componentProps: {
      description: t(`CheckoutOrderDetail:${ORDER_DETAIL_ITEMS_ID.MIN_DELIVERY_FEE_TEXT}`),
      amount: formatPrice(minimumValueForFreeDelivery),
      hide: !showMinDeliveryFreeText,
      className: styles.deliveryMinBox,
      amountClassName: styles.deliveryMinBoxAmount
    }
  },
  {
    id: ORDER_DETAIL_ITEMS_ID.SEPARATOR,
    Component: () => (showTip ? <hr className={cn(styles.itemSeparator)} /> : null)
  },
  {
    id: ORDER_DETAIL_ITEMS_ID.TIP_SELECTION,
    Component: TipSelection,
    componentProps: {
      selectedPaymentMethod,
      isPapaCar
    }
  },
  {
    id: ORDER_DETAIL_ITEMS_ID.TIP,
    Component: ListItem,
    componentProps: {
      className: styles.tipContainer,
      description: t(`CheckoutOrderDetail:${ORDER_DETAIL_ITEMS_ID.TIP}`),
      amount: formatPrice(tip),
      hide: !showTip
    }
  },
  {
    id: ORDER_DETAIL_ITEMS_ID.PAPA_POINTS_USED,
    Component: ListItem,
    componentProps: {
      className: styles.marginBottom,
      description: t(`CheckoutOrderDetail:${ORDER_DETAIL_ITEMS_ID.PAPA_POINTS_USED}`),
      amount: pointsSpent,
      hide: !showPointsSpent
    }
  }
];
